import React, {useCallback, useRef} from 'react';
import {AutoSizer, CellMeasurer, CellMeasurerCache, MultiGrid, WindowScroller} from 'react-virtualized';
import Input from '../../../../components/bootstrap/forms/Input';
import Icon from '../../../../components/icon/Icon';
import {useTranslation} from 'react-i18next';

const DELETE_KEY = '__delete__';
const DELETE_COL_WIDTH = 56;
const RESULT_COL_WIDTH = 320;

const checkValid = (str) => {
    if (str.charAt(0) === '#' && str.charAt(str.length - 1) === '#') {
        const inner = str.substring(1, str.length - 1);
        return inner === 'Invalid date' ? '' : inner;
    }
    return str;
};

const ValidMultiGrid = (props) => {
    const listRef = useRef(null);
    const {dataList, setData, allUserList, templates, onDeleteRow, validateCell, formatCell} = props;
    const baseColData = Object.keys(dataList[0]);
    const colData = onDeleteRow ? [...baseColData, DELETE_KEY] : baseColData;
    const {t} = useTranslation();

    const gridKeyRef = useRef(0);
    const prevLengthRef = useRef(dataList.length);

    if (dataList.length !== prevLengthRef.current) {
        prevLengthRef.current = dataList.length;
        gridKeyRef.current += 1;
    }

    const cellCache = useRef(new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 50,
        minWidth: 146,
        fixedHeight: false,
    })).current;

    const TDCell = useCallback(({ columnIndex, dataKey, rowData, rowIndex }) => {
        if (dataKey === DELETE_KEY) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <button
                        type='button'
                        onClick={() => onDeleteRow && onDeleteRow(rowIndex - 1)}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: 8,
                            background: 'rgba(239,68,68,0.08)',
                            border: '1px solid rgba(239,68,68,0.2)',
                            cursor: 'pointer', flexShrink: 0,
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.background = '#ef4444';
                            e.currentTarget.querySelector('span').style.color = '#fff';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.background = 'rgba(239,68,68,0.08)';
                            e.currentTarget.querySelector('span').style.color = '#ef4444';
                        }}
                    >
                        <span style={{ color: '#ef4444', fontSize: '1rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                            <Icon icon='Delete' style={{ fontSize: '1rem' }} />
                        </span>
                    </button>
                </div>
            );
        }

        if (dataKey === t('결과 메시지')) {
            const val = rowData[dataKey] ?? '';
            const isNormal = val === '정상';
            const errors = isNormal ? [] : val.split('||');
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '4px 6px' }}>
                    {isNormal ? (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: '0.82rem', fontWeight: 700,
                            color: '#0ab39c', background: 'rgba(10,179,156,0.08)',
                            borderRadius: 20, padding: '3px 12px',
                            whiteSpace: 'nowrap',
                        }}>
                            ✓ {val}
                        </span>
                    ) : errors.map((err, i) => (
                        <span key={i} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: '0.75rem', fontWeight: 700,
                            color: '#ef4444', background: 'rgba(239,68,68,0.07)',
                            border: '1px solid rgba(239,68,68,0.25)',
                            borderRadius: 6, padding: '3px 8px',
                            whiteSpace: 'nowrap', lineHeight: 1.4,
                        }}>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                            {err}
                        </span>
                    ))}
                </div>
            );
        }

        const isInvalid = (raw) => {
            if (validateCell) return validateCell(dataKey, raw, rowData);
            const val = checkValid(raw ?? '');
            return !val.trim() || val.length !== (raw ?? '').length;
        };

        switch (dataKey) {
            case t('이름'): case t('템플릿 이름'):
                return (
                    <div>
                        <Input
                            size='sm'
                            className={isInvalid(rowData[dataKey]) ? 'border-danger' : ''}
                            value={checkValid(rowData[dataKey])}
                            readOnly
                        />
                    </div>
                );
            default:
                return (
                    <div>
                        <Input
                            size='sm'
                            className={isInvalid(rowData[dataKey]) ? 'border-danger' : ''}
                            value={checkValid(rowData[dataKey] ?? '')}
                            onKeyDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                const edit = [...dataList];
                                if (dataKey === t('사번')) {
                                    edit[rowIndex - 1][t('이름')] = allUserList?.find?.(
                                        user => user.companyNumber === e.target.value
                                    )?.user?.name ?? '';
                                }
                                if (dataKey === t('템플릿 코드')) {
                                    const lookupCode = e.target.value.trim() || 'F';
                                    edit[rowIndex - 1][t('템플릿 이름')] = templates?.find?.(
                                        tmp => tmp.code === lookupCode
                                    )?.name ?? '';
                                }
                                const formatted = formatCell ? formatCell(dataKey, e.target.value) : e.target.value;
                                edit[rowIndex - 1][dataKey] = formatted;
                                setData(edit, rowIndex - 1);
                            }}
                        />
                    </div>
                );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataList, onDeleteRow]);

    const trName = (value) => {
        if (value === DELETE_KEY) return '';
        const tr = key => t(`common.downloadExcelAn.rest.${key}`);
        switch (value) {
            case '이름': return tr('name');
            case '이메일': return tr('email');
            case '사용 시작일자': return tr('usedStartDate');
            case '사용 종료일자': return tr('usedEndDate');
            case '휴가유형': return tr('vacationType');
            default: return value;
        }
    };

    const cellRenderer = useCallback(({ columnIndex, rowIndex, style, parent }) => {
        const dataKey = colData[columnIndex];
        const isDeleteCol = dataKey === DELETE_KEY;

        return rowIndex === 0 ? (
            <CellMeasurer cache={cellCache} parent={parent} columnIndex={columnIndex} rowIndex={rowIndex}>
                <div
                    style={{ ...style, background: isDeleteCol ? 'transparent' : undefined }}
                    className='py-3 text-nowrap text-center'
                >
                    {trName(dataKey)}
                </div>
            </CellMeasurer>
        ) : (
            <CellMeasurer cache={cellCache} parent={parent} columnIndex={columnIndex} rowIndex={rowIndex}>
                <div
                    style={style}
                    className={[
                        isDeleteCol ? 'd-flex align-items-center justify-content-center' : 'p-2 text-nowrap text-center',
                        'border-bottom border-end',
                        columnIndex === 8 ? 'bg-l25-info' :
                        columnIndex === 9 ? 'bg-light' :
                        rowIndex % 2 === 0 ? 'bg-l25-light' : '',
                    ].join(' ')}
                >
                    {TDCell({ columnIndex, dataKey, parent, rowData: dataList[rowIndex - 1], rowIndex })}
                </div>
            </CellMeasurer>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataList, onDeleteRow]);

    return (
        <WindowScroller>
            {() => (
                <AutoSizer onResize={() => listRef.current?.recomputeGridSize()}>
                    {({ width, height }) => {
                        const lastDataIdx = baseColData.length - 1;
                        const isResultLast = baseColData[lastDataIdx] === t('결과 메시지');
                        const deleteColWidth = onDeleteRow ? DELETE_COL_WIDTH : 0;
                        const resultColWidth = isResultLast ? RESULT_COL_WIDTH : 0;
                        const remainingCols = colData.length - (onDeleteRow ? 1 : 0) - (isResultLast ? 1 : 0);
                        const otherColWidth = Math.max(120, (width - deleteColWidth - resultColWidth) / remainingCols);

                        const customColWidth = ({ index }) => {
                            const key = colData[index];
                            if (key === DELETE_KEY) return DELETE_COL_WIDTH;
                            if (key === t('결과 메시지')) return RESULT_COL_WIDTH;
                            return otherColWidth;
                        };

                        return (
                            <MultiGrid
                                key={gridKeyRef.current}
                                ref={listRef}
                                cellRenderer={cellRenderer}
                                classNameTopLeftGrid='border-light border-end border-bottom fw-bold'
                                classNameTopRightGrid='border-light border-bottom fw-bold'
                                classNameBottomLeftGrid='border-light border-end'
                                fixedColumnCount={1}
                                fixedRowCount={1}
                                height={height}
                                width={width}
                                overscanRowCount={5}
                                overscanColumnCount={5}
                                columnCount={colData.length}
                                columnWidth={customColWidth}
                                rowCount={dataList.length + 1}
                                rowHeight={cellCache.rowHeight}
                            />
                        );
                    }}
                </AutoSizer>
            )}
        </WindowScroller>
    );
};

export default React.memo(ValidMultiGrid);
